<script>
    import { onMount } from 'svelte';
    import { labelsAPI } from './api.js';
    import { toast } from './lib/toast.js';
    import Icon from './lib/Icon.svelte';

    let labels = $state([]);
    let loading = $state(true);
    let showModal = $state(false);
    let editing = $state(null);
    let form = $state({ name: '', color: '#2196F3', description: '' });

    const PALETTE = ['#2196F3', '#4caf50', '#ff5722', '#9c27b0', '#ff9800',
                     '#e91e63', '#00bcd4', '#795548', '#607d8b', '#f44336'];

    async function load() {
        loading = true;
        try {
            labels = await labelsAPI.getAll();
        } catch (e) {
            toast.error(e.message || 'No se pudieron cargar las etiquetas');
        } finally {
            loading = false;
        }
    }
    onMount(load);

    function openNew() {
        editing = null;
        form = { name: '', color: PALETTE[0], description: '' };
        showModal = true;
    }
    function openEdit(label) {
        editing = label;
        form = { name: label.name, color: label.color || PALETTE[0], description: label.description || '' };
        showModal = true;
    }

    async function save() {
        if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
        try {
            if (editing) {
                await labelsAPI.update(editing.id, form);
                toast.success('Etiqueta actualizada');
            } else {
                await labelsAPI.create(form);
                toast.success('Etiqueta creada');
            }
            showModal = false;
            await load();
        } catch (e) {
            toast.error(e.message || 'No se pudo guardar');
        }
    }

    async function remove(label) {
        if (!confirm(`¿Eliminar la etiqueta "${label.name}"? Se quitará de todas las conversaciones y contactos.`)) return;
        try {
            await labelsAPI.delete(label.id);
            toast.success('Etiqueta eliminada');
            await load();
        } catch (e) {
            toast.error(e.message || 'No se pudo eliminar');
        }
    }
</script>

<div class="labels-view">
    <div class="lv-header">
        <p class="lv-sub">Etiquetas de tu organización para clasificar conversaciones y contactos.</p>
        <button class="btn-primary" onclick={openNew}>
            <Icon name="plus" size={14} /> Nueva etiqueta
        </button>
    </div>

    {#if loading}
        <p class="lv-empty">Cargando…</p>
    {:else if !labels.length}
        <p class="lv-empty">Aún no hay etiquetas. Crea la primera.</p>
    {:else}
        <div class="lv-list">
            {#each labels as label (label.id)}
                <div class="lv-item">
                    <span class="lv-chip" style="background:{label.color}">{label.name}</span>
                    {#if label.description}<span class="lv-desc">{label.description}</span>{/if}
                    <div class="lv-actions">
                        <button class="lv-icon" title="Editar" onclick={() => openEdit(label)} aria-label="Editar"><Icon name="edit" size={13} /></button>
                        <button class="lv-icon danger" title="Eliminar" onclick={() => remove(label)} aria-label="Eliminar"><Icon name="trash" size={13} /></button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

{#if showModal}
    <div class="modal-backdrop" onclick={() => showModal = false} role="presentation">
        <div class="modal" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
            <h3>{editing ? 'Editar etiqueta' : 'Nueva etiqueta'}</h3>
            <label>Nombre
                <input type="text" bind:value={form.name} placeholder="VIP, Urgente, Cliente…" maxlength="50" />
            </label>
            <label>Color
                <div class="color-row">
                    {#each PALETTE as c}
                        <button class="swatch" class:sel={form.color === c} style="background:{c}"
                                onclick={() => form.color = c} aria-label="Color {c}"></button>
                    {/each}
                </div>
            </label>
            <label>Descripción (opcional)
                <input type="text" bind:value={form.description} maxlength="255" />
            </label>
            <div class="modal-actions">
                <button class="btn-ghost" onclick={() => showModal = false}>Cancelar</button>
                <button class="btn-primary" onclick={save}>{editing ? 'Guardar' : 'Crear'}</button>
            </div>
        </div>
    </div>
{/if}

<style>
    .labels-view { padding: 4px 2px; }
    .lv-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
    .lv-sub { color: var(--text-2); font-size: 0.9rem; margin: 0; }
    .lv-empty { color: var(--text-3); padding: 24px 0; }
    .lv-list { display: flex; flex-direction: column; gap: 8px; }
    .lv-item { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: var(--surface-0);
               border: 1px solid var(--border-1); border-radius: 10px; }
    .lv-chip { color: #fff; font-weight: 600; font-size: 0.82rem; padding: 3px 12px; border-radius: 999px; }
    .lv-desc { color: var(--text-2); font-size: 0.85rem; }
    .lv-actions { margin-left: auto; display: flex; gap: 6px; }
    .lv-icon { border: none; background: transparent; cursor: pointer; color: var(--text-2); padding: 6px; border-radius: 6px; }
    .lv-icon:hover { background: var(--surface-1); color: var(--text-1); }
    .lv-icon.danger:hover { color: var(--danger); }
    .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center;
                      justify-content: center; z-index: 1000; }
    .modal { background: var(--surface-0); border-radius: 14px; padding: 22px; width: 380px; max-width: 92vw;
             display: flex; flex-direction: column; gap: 12px; }
    .modal h3 { margin: 0 0 4px; }
    .modal label { display: flex; flex-direction: column; gap: 6px; font-size: 0.85rem; color: var(--text-2); }
    .modal input { padding: 8px 10px; border: 1px solid var(--border-1); border-radius: 8px; background: var(--surface-1); color: var(--text-1); }
    .color-row { display: flex; flex-wrap: wrap; gap: 6px; }
    .swatch { width: 26px; height: 26px; border-radius: 50%; border: 2px solid transparent; cursor: pointer; }
    .swatch.sel { border-color: var(--text-1); transform: scale(1.1); }
    .modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; }
    .btn-primary { background: var(--accent); color: #fff; border: none; padding: 8px 16px; border-radius: 8px; cursor: pointer;
                   display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
    .btn-ghost { background: transparent; border: 1px solid var(--border-1); color: var(--text-2); padding: 8px 16px; border-radius: 8px; cursor: pointer; }
</style>
